import {controlStyle, fieldLabelStyle} from '../lib/formStyles.js';
import {
  RuminantGroupLabel,
  RuminantFlagBadge,
  ruminantFlagStyle,
  formatRuminantAge,
  formatRuminantAgeAtDate,
  ruminantFilterChipBaseStyle,
  ruminantFilterChipActiveStyle,
  ruminantSavedViewRadioLabelStyle,
  modalBackdropStyle,
  modalDialogStyle,
} from './ruminantPresentation.js';
// CattleHerdsView — grouped default herds + flat filtered/sorted results
// + composable filter chips + ordered sort rules + saved views
// (surface_key cattle.herds). The local smart-filter assistant was removed. A
// future AI-assisted filter/sort requires its own product decision.
//
// Filter / sort semantics live in src/lib/cattleHerdFilters.js (pure module,
// vitest-locked). This file is UI wiring only.
//
// Maternal-issue UI is retired. DB columns
// `cattle.maternal_issue_flag` + `cattle.maternal_issue_desc` are NOT dropped
// — that's a separate migration gate.
import React from 'react';
import type {CSSProperties, MouseEventHandler, ReactNode} from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import CattleAnimalPage from './CattleAnimalPage.tsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleHerdsCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {CattleAnimalCreateVars, CattleOriginCreateVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import {
  buildCattlePredicate,
  buildCattleComparator,
  mergeObservedValues,
  cowTagSet,
  lastWeightFor,
  lastWeightEntryFor,
  calfCountFor,
  buildCalvingEvidence,
  lastCalvingRecordFor,
  CATTLE_HERD_KEYS,
  CATTLE_OUTCOME_KEYS,
  CATTLE_ALL_HERD_KEYS,
  CATTLE_SORT_KEYS,
  STALE_WEIGHT_DAYS_DEFAULT,
} from '../lib/cattleHerdFilters.js';
import type {CattleCalvingRecord, CattleFilters, CattleOldTag, MergedOptionValue} from '../lib/cattleHerdFilters.js';
import {createSavedView, updateSavedView, deleteSavedView, buildViewState} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Button from '../shared/Button.js';
import DataTable from '../shared/DataTable.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import StatusText from '../shared/StatusText.jsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {printRows} from '../lib/printExport.js';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {AuthIdentity, AuthState} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import Header from '../shared/Header.tsx';
import {canBulkImportLivestock, canWriteLivestockTables, type AuthState as AccessAuthState} from '../lib/access.js';
import './cattleRedesign.css';
import './cattleHerdsRedesign.css';
import {useRecordActivityLatest} from '../shared/recordActivityLatestData.js';
import {
  accountingSnapshotMaxMonth,
  accountingSnapshotMinMonth,
  accountingSnapshotMonthEndISO,
  accountingSnapshotRows,
  formatAccountingMonthEnd,
} from '../lib/accountingMonthEndSnapshot.js';

// Opened only from its button, and an import needs the network anyway: loaded on
// demand so it stays out of the startup bundle (as CutSheetPickerModal is).
const CattleBulkImport = React.lazy(() => import('./CattleBulkImport.tsx'));

type ErrorLike = {message?: string};

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the notice text the `{error}`
// envelope produced — including the "undefined" a message-less error would have
// rendered before. This is a conversion, not a rewording.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as ErrorLike).message) : String(err);
type CattleHerdsProps = Record<string, never>;
type OldTagStored = CattleOldTag & {changed_at?: string | null};
type OldTagDraft = {tag: string | number; changed_at: string; source: string};
type OldTagWrite = {tag: string; changed_at?: string; source?: string};
type CattleHerdRow = {
  [column: string]: unknown;
  id: string;
  tag: string | null;
  herd: string;
  sex: string | null;
  breed: string | null;
  origin: string | null;
  birth_date: string | null;
  purchase_date: string | null;
  purchase_amount: number | string | null;
  dam_tag: string | null;
  sire_tag: string | null;
  registration_num: string | null;
  breeding_status: string | null;
  breeding_blacklist: boolean;
  pct_wagyu: number | null;
  sale_date: string | null;
  sale_amount: number | string | null;
  death_date: string | null;
  death_reason: string | null;
  old_tags: OldTagStored[];
  created_at: string;
  deleted_at?: string | null;
  _accountingSnapshotMonth?: string;
  _accountingSnapshotEndDate?: string;
  _accountingSnapshotOriginalGroup?: string | null;
};
type CattleWeighIn = {
  [column: string]: unknown;
  tag?: string | number | null;
  weight?: number | string | null;
  entered_at?: string | null;
};
type CalvingRow = CattleCalvingRecord & {
  [column: string]: unknown;
  id: string;
  dam_tag: string;
  calving_date: string;
  complications_flag?: boolean;
  complications_desc?: string | null;
  notes?: string | null;
};
type OptionRow = {[column: string]: unknown; id: string; label: string; active: boolean};
type CattleTransferRow = {
  [column: string]: unknown;
  id: string;
  cattle_id: string;
  from_herd: string | null;
  to_herd: string;
  transferred_at: string;
};
type HerdsData = Omit<
  CattleHerdsCoreData,
  'cattle' | 'weighIns' | 'calvingRecords' | 'breedOptions' | 'originOptions' | 'transfers'
> & {
  cattle: CattleHerdRow[];
  weighIns: CattleWeighIn[];
  calvingRecords: CalvingRow[];
  breedOptions: OptionRow[];
  originOptions: OptionRow[];
  transfers: CattleTransferRow[];
};
type HerdResource = Omit<ReturnType<typeof useRouteResource>, 'data' | 'error'> & {
  data: HerdsData | null;
  error: ErrorLike | null;
};
type FilterState = CattleFilters & Record<string, unknown>;
type SortDir = 'asc' | 'desc';
type SortRule = {key: string; dir: SortDir};
type LastActivityCache = {
  byCowId: Record<string, string | null | undefined>;
  loadKey: string;
  loadedAt: string | null;
};
type SavedViewState = {filters?: unknown; sortRules?: unknown; columns?: unknown};
type SavedViewRow = {
  id: string;
  name: string;
  visibility: string;
  owner_profile_id: string | null;
  view_state: unknown;
};
type CowForm = {
  tag: string;
  sex: string;
  herd: string;
  breed: string;
  breeding_blacklist: boolean;
  pct_wagyu: string;
  origin: string;
  birth_date: string;
  purchase_date: string;
  purchase_amount: string;
  dam_tag: string;
  sire_tag: string;
  registration_num: string;
  breeding_status: string;
  sale_date: string;
  sale_amount: string;
  death_date: string;
  death_reason: string;
  old_tags: OldTagDraft[];
};
type CowWrite = {
  tag: string | null;
  sex: string;
  herd: string;
  breed: string | null;
  breeding_blacklist: boolean;
  pct_wagyu: number | null;
  origin: string | null;
  birth_date: string | null;
  purchase_date: string | null;
  purchase_amount: number | null;
  dam_tag: string | null;
  sire_tag: string | null;
  registration_num: string | null;
  breeding_status: string | null;
  sale_date: string | null;
  sale_amount: number | null;
  death_date: string | null;
  death_reason: string | null;
  old_tags: OldTagWrite[];
};
type PersistentSetter<T> = (next: T | ((previous: T) => T)) => void;
type FilterChipProps = {
  label: ReactNode;
  active: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  dataAttr?: string;
};
type CattleTableColumn = {
  key: string;
  label: ReactNode;
  align?: 'left' | 'right';
  primary?: boolean;
  render?: (row: CattleHerdRow) => ReactNode;
};
type CattleExportColumn = {header: string; value: (row: CattleHerdRow) => unknown};
type CattleDataTableProps = {navList: CattleHerdRow[]; surfaceKey: string; grouped?: boolean};
type FilterChipPopoverProps = {
  filterKey: string;
  filters: FilterState;
  setFilters: PersistentSetter<FilterState>;
  setFilter: (key: string, value: unknown) => void;
  clearFilter: (key: string) => void;
  toggleFilterArrayValue: (key: string, value: string) => void;
  breedFilterOptions: MergedOptionValue[];
  originFilterOptions: MergedOptionValue[];
  onClose: () => void;
};
type PopoverFooterProps = {onClear: () => void; onClose: () => void};

const HERD_LABELS: Record<string, string> = {
  mommas: 'Mommas',
  backgrounders: 'Backgrounders',
  finishers: 'Finishers',
  bulls: 'Bulls',
  processed: 'Processed',
  deceased: 'Deceased',
  sold: 'Sold',
};

// Toggleable columns for the cattle list (the column/display picker). `tag` is
// always shown; the rest can be hidden/shown, and the choice is stored in saved
// views. Defaults keep today's visible set on.
const CATTLE_HERD_COLUMNS: {key: string; label: string; default: boolean}[] = [
  {key: 'herd', label: 'Herd', default: true},
  {key: 'sex', label: 'Sex', default: true},
  {key: 'breed', label: 'Breed', default: true},
  {key: 'origin', label: 'Origin', default: true},
  {key: 'age', label: 'Age', default: true},
  {key: 'birthDate', label: 'Birth Date', default: false},
  {key: 'lastWeight', label: 'Last Weight', default: true},
  {key: 'lastWeighed', label: 'Last Weighed', default: false},
  {key: 'lastActivity', label: 'Last Activity', default: false},
  {key: 'lastCalved', label: 'Last Calved', default: true},
  {key: 'calfCount', label: 'Calf Count', default: true},
  {key: 'breedingStatus', label: 'Breeding Status', default: false},
  {key: 'breedingBlacklist', label: 'Breeding Blacklist', default: false},
  {key: 'damTag', label: 'Dam Tag', default: false},
  {key: 'sireTag', label: 'Sire Tag', default: false},
  {key: 'pctWagyu', label: '% Wagyu', default: false},
  {key: 'registrationNum', label: 'Registration #', default: false},
  {key: 'purchaseDate', label: 'Purchase Date', default: false},
  {key: 'purchaseAmount', label: 'Purchase Amount', default: false},
  {key: 'saleDate', label: 'Sale Date', default: false},
  {key: 'saleAmount', label: 'Sale Amount', default: false},
  {key: 'deathDate', label: 'Death Date', default: false},
  {key: 'deathReason', label: 'Death Reason', default: false},
  {key: 'recordId', label: 'Record ID', default: false},
];
const CATTLE_DEFAULT_COLUMN_KEYS = CATTLE_HERD_COLUMNS.filter((c) => c.default).map((c) => c.key);

// One column definition drives both the header and every row, including saved
// optional columns. The shared DataTable keeps its record/keyboard behavior.
const CATTLE_COLUMN_WIDTHS: Record<string, number> = {
  tag: 64,
  herd: 100,
  snapshotHerd: 116,
  sex: 64,
  breed: 160,
  origin: 170,
  age: 64,
  lastWeight: 92,
  lastCalved: 104,
  calfCount: 64,
  lastActivity: 180,
  breedingStatus: 130,
  breedingBlacklist: 146,
  registrationNum: 148,
  deathReason: 180,
  recordId: 290,
};

function HerdsIcon({name}: {name: 'search' | 'calendar' | 'chevron' | 'plus'}) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {name === 'search' && (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </>
      )}
      {name === 'calendar' && (
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </>
      )}
      {name === 'chevron' && <path d="m9 6 6 6-6 6" />}
      {name === 'plus' && <path d="M12 5v14M5 12h14" />}
    </svg>
  );
}

const SEX_OPTIONS = [
  {key: 'cow', label: 'Cow'},
  {key: 'heifer', label: 'Heifer'},
  {key: 'bull', label: 'Bull'},
  {key: 'steer', label: 'Steer'},
];

const BREEDING_STATUS_OPTIONS = [
  {key: 'OPEN', label: 'Open'},
  {key: 'PREGNANT', label: 'Pregnant'},
  {key: 'N/A', label: 'N/A'},
  {key: 'unset', label: '(unset)'},
];

const SORT_KEY_LABELS: Record<string, string> = {
  tag: 'Tag',
  age: 'Age',
  lastWeight: 'Last weight',
  lastActivity: 'Last activity',
  herd: 'Herd',
  sex: 'Sex',
  lastCalved: 'Last calved',
  calfCount: 'Calf count',
  nonCalving: 'Non-calving',
  breed: 'Breed',
  origin: 'Origin',
};

const SORT_DIR_LABELS: Record<string, Record<SortDir, string>> = {
  tag: {asc: '↑ low→high', desc: '↓ high→low'},
  age: {asc: 'youngest first', desc: 'oldest first'},
  lastWeight: {asc: 'lightest first', desc: 'heaviest first'},
  lastActivity: {asc: 'oldest first', desc: 'newest first'},
  herd: {asc: '↑ active→outcome', desc: '↓ outcome→active'},
  sex: {asc: '↑ cow→steer', desc: '↓ steer→cow'},
  lastCalved: {asc: 'oldest first', desc: 'most recent first'},
  calfCount: {asc: 'fewest first', desc: 'most first'},
  nonCalving: {asc: 'candidates last', desc: 'candidates first'},
  breed: {asc: '↑ A→Z', desc: '↓ Z→A'},
  origin: {asc: '↑ A→Z', desc: '↓ Z→A'},
};
const SORT_DEFAULT_DIR: Partial<Record<string, SortDir>> = {
  lastActivity: 'desc',
};

// Saved-views surface key for the cattle herds list (migration 095).
// Frozen: the resource key is derived from these, and a fresh object each
// render would change the key every render and refetch forever.
const HERDS_PARAMS = Object.freeze({});

const CATTLE_HERDS_SURFACE_KEY = 'cattle.herds';
const DEFAULT_SORT_RULES: readonly SortRule[] = Object.freeze([{key: 'tag', dir: 'asc'}]);
const EMPTY_LAST_ACTIVITY_CACHE: LastActivityCache = {byCowId: {}, loadKey: '', loadedAt: null};

function normalizeLastActivityCache(cache: unknown): LastActivityCache {
  if (!cache || typeof cache !== 'object' || Array.isArray(cache)) return EMPTY_LAST_ACTIVITY_CACHE;
  const stored = cache as Record<string, unknown>;
  const byCowId =
    stored.byCowId && typeof stored.byCowId === 'object' && !Array.isArray(stored.byCowId)
      ? (stored.byCowId as Record<string, string | null | undefined>)
      : {};
  return {
    byCowId,
    loadKey: typeof stored.loadKey === 'string' ? stored.loadKey : '',
    loadedAt: typeof stored.loadedAt === 'string' ? stored.loadedAt : null,
  };
}

function hasCachedActivityValue(byCowId: Record<string, string | null | undefined>, id: string) {
  return Object.prototype.hasOwnProperty.call(byCowId, id);
}

function isDefaultSortRules(rules: readonly SortRule[] | null | undefined) {
  return (
    Array.isArray(rules) &&
    rules.length === 1 &&
    rules[0] &&
    rules[0].key === DEFAULT_SORT_RULES[0].key &&
    rules[0].dir === DEFAULT_SORT_RULES[0].dir
  );
}

function filterValueActive(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.values(value).some(filterValueActive);
  return true;
}

function activeFilterKeysFor(filters: Record<string, unknown> | null | undefined): string[] {
  const source = filters || {};
  return Object.keys(source).filter((key) => filterValueActive(source[key]));
}

function formatActivityDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Organized filter groups (replaces the old quick/More-filters split). Each
// filter is always visible under its group. 'nonCalving' is a composite chip
// (boolean toggle + "no calf since" date); 'unmatchedCalves' is a boolean
// toggle chip. Everything else opens a value popover.
const FILTER_GROUPS = [
  {key: 'core', label: 'Core', keys: ['herdSet', 'sex', 'ageMonthsRange', 'breed', 'origin', 'weightTier']},
  {
    key: 'calving',
    label: 'Calving/Breeding',
    keys: ['nonCalving', 'calvedStatus', 'lastCalvedRange', 'calfCountRange', 'breedingStatus', 'breedingBlacklist'],
  },
  {
    key: 'lineage',
    label: 'Lineage/Other',
    // Unmatched Calves is a lineage/data-relationship check (no matched dam),
    // rendered as a checkbox-style filter pushed off to the right side of the
    // Lineage/Other row (not mid-row among the chip filters).
    keys: ['damPresence', 'sirePresence', 'birthDateRange', 'wagyuPctRange', 'weightRange', 'unmatchedCalves'],
  },
];
// Checkbox-style filters (a labeled checkbox, not a pill/popover chip).
const CHECKBOX_FILTER_KEYS = new Set(['unmatchedCalves']);
const CHECKBOX_FILTER_LABELS: Record<string, string> = {unmatchedCalves: 'Unmatched Calves'};

const EMPTY_COW = {
  tag: '',
  sex: 'cow',
  herd: 'mommas',
  breed: '',
  breeding_blacklist: false,
  pct_wagyu: '',
  origin: '',
  birth_date: '',
  purchase_date: '',
  purchase_amount: '',
  dam_tag: '',
  sire_tag: '',
  registration_num: '',
  breeding_status: '',
  sale_date: '',
  sale_amount: '',
  death_date: '',
  death_reason: '',
  old_tags: [],
};

const inpS: CSSProperties = controlStyle;
const lbl: CSSProperties = fieldLabelStyle;

// Selection controls share the Constitution green accent; group identity is a dot.
const CATTLE_ACCENT = 'var(--brand)';
const chipBaseS: CSSProperties = ruminantFilterChipBaseStyle;
const chipActiveS: CSSProperties = ruminantFilterChipActiveStyle;

function CattleHerdsRouter(props: CattleHerdsProps) {
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    authState: AuthIdentity | null;
  };
  const location = useLocation();
  const cattleDetailId = location.pathname.startsWith('/cattle/herds/')
    ? location.pathname.slice('/cattle/herds/'.length) || null
    : null;
  if (cattleDetailId) {
    return React.createElement(CattleAnimalPage, {
      sb,
      fmt,
      authState,
    });
  }
  return React.createElement(CattleHerdsHub, props);
}

export const CattleHerdsHub: React.FC<CattleHerdsProps> = () => {
  const {authState, rolePreview} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState' | 'rolePreview'> & {
    authState: AuthIdentity | null;
    rolePreview?: string;
  };
  const {setView, pendingEdit, setPendingEdit} = useUI();
  const {useState, useEffect, useMemo} = React;
  const navigate = useNavigate();
  // B3.3 PILOT: the hub's six reads now come from the cattle.herds.core
  // resource instead of a local loadAll. Same six queries, same sort orders —
  // the change is WHERE they live and how failure is reported. The old catch
  // blanked all six lists AND set an error, so the surface rendered "no cattle"
  // and an error banner simultaneously; the resource contract makes that one
  // error state with a Retry.
  const herds = useRouteResource(RESOURCE.CATTLE_HERDS_CORE, HERDS_PARAMS) as HerdResource;
  const cattle = herds.data ? herds.data.cattle : [];
  const weighIns = herds.data ? herds.data.weighIns : [];
  const calvingRecs = herds.data ? herds.data.calvingRecords : [];
  // Inline notice for save / patch / calving validation failures.
  // Cleared on each save attempt + on form open so a prior row's error
  // never shadows the next flow.
  const [notice, setNotice] = useState<Notice | null>(null);
  const breedOpts = herds.data ? herds.data.breedOptions : [];
  const originOpts = herds.data ? herds.data.originOptions : [];
  // Inline new-origin UI: when the operator picks "+ Add new origin…" in the
  // form's Origin select, we expose a small text input next to the select
  // instead of opening a native window.prompt. addingOrigin gates render;
  // newOriginInput holds the in-progress label.
  const [addingOrigin, setAddingOrigin] = useState(false);
  const [newOriginInput, setNewOriginInput] = useState('');
  const cattleTransfers = herds.data ? herds.data.transfers : [];
  const loading = herds.loading;
  // The resource throws a real Error; the surface's banner takes {kind,message}.
  const loadError: Notice | null = herds.error
    ? {
        kind: 'error',
        message: 'Could not load cattle herds. Please retry. (' + (herds.error.message || herds.error) + ')',
      }
    : null;
  // Every existing caller (refresh button, post-mutation reloads, bulk-import
  // onComplete) keeps working: loadAll is now the resource's refetch.
  const loadAll = herds.refetch;
  const createOrigin = useRouteMutation<CattleOriginCreateVars, {id: string; label: string; active: boolean} | null>(
    MUTATION.CATTLE_ORIGIN_CREATE,
  );
  // The `cattle` row itself: the Add Cow create. (The edit branch and
  // `patchCow` were unreachable and went in E12a; edits are the record page's.)
  const createCattleAnimal = useRouteMutation<CattleAnimalCreateVars, null>(MUTATION.CATTLE_ANIMAL_CREATE);

  // Composable filter / sort state.
  const [filters, setFilters] = usePersistentViewState('cattle.herds.filters', {} as FilterState);
  const initialSortRules = [{...DEFAULT_SORT_RULES[0]}] as SortRule[];
  const [sortRules, setSortRules] = usePersistentViewState('cattle.herds.sortRules', initialSortRules);
  // Fresh views start with Mommas open; existing saved expansion choices win.
  const initialExpandedHerds = {mommas: true} as Record<string, boolean>;
  const [expandedHerds, setExpandedHerds] = usePersistentViewState('cattle.herds.expandedHerds', initialExpandedHerds);
  const [lastActivityCache, setLastActivityCache] = usePersistentViewState(
    'cattle.herds.lastActivityCache',
    EMPTY_LAST_ACTIVITY_CACHE,
  );
  const lastActivityByCowId = normalizeLastActivityCache(lastActivityCache).byCowId;
  const lastActivityCacheRef = React.useRef(lastActivityCache);
  const [lastActivityLoading, setLastActivityLoading] = useState(false);
  const [lastActivityNotice, setLastActivityNotice] = useState<Notice | null>(null);
  const hasLastActivitySort = sortRules.some((r) => r && r.key === 'lastActivity');
  // Which columns show in the flat cattle list (the column/display picker),
  // persisted per surface and stored in saved views. `tag` is always shown.
  const [visibleColumns, setVisibleColumns] = usePersistentViewState(
    'cattle.herds.columns',
    CATTLE_DEFAULT_COLUMN_KEYS,
  );
  function toggleColumn(key: string) {
    setVisibleColumns((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  }
  const columnVisible = (key: string) =>
    key === 'tag' || (key === 'lastActivity' && hasLastActivitySort) || visibleColumns.includes(key);
  const [openFilter, setOpenFilter] = useState<string | null>(null); // chip popover state — string key
  const [openToolPanel, setOpenToolPanel] = useState<string | null>(null); // savedViews | filters | sort | columns | snapshot

  // Saved views (migration 095). Load failures must NOT break the list — they
  // surface as a disabled control + inline message (cold-boot safety).
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState<'private' | 'public'>('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const myProfileId = authState?.user?.id || null;
  const savedViewsQuery = useSavedViewsData(CATTLE_HERDS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;

  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [form, setForm] = useState<CowForm | null>(null);
  const [saving, setSaving] = useState(false);

  // loadAll and its mount effect are gone: the resource owns fetching,
  // cancellation and the key. `loadAll` above is its refetch.

  // Saved views stay independent of the cattle list resource: a table/RLS
  // failure degrades the picker only and never blocks the herd list.
  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  // ── helpers (the lib owns the math; these are display-side wrappers) ──────
  function age(birth: string | null | undefined) {
    return formatRuminantAge(birth, 'm', true);
  }
  function ageAtDate(birth: string | null | undefined, endDate: string | null | undefined) {
    return formatRuminantAgeAtDate(birth, endDate, 'm');
  }
  function rowAge(row: CattleHerdRow) {
    return row?._accountingSnapshotEndDate
      ? ageAtDate(row.birth_date, row._accountingSnapshotEndDate)
      : age(row.birth_date);
  }
  function lastWeight(cow: CattleHerdRow) {
    return lastWeightFor(cow, weighIns);
  }
  // Aggregate herd-tile total uses default for recently-purchased cows without
  // a real weigh-in. Per-cow rows still show "no weigh-in" honestly.
  // ── filter + sort: pure-helper-driven ─────────────────────────────────────
  // Cattle remain in one predicate/sort pipeline. The render layer decides
  // whether the matching rows display grouped by herd (default) or as one flat
  // results table (any active filter or sort).
  const breedFilterOptions = useMemo(
    () => mergeObservedValues(breedOpts, [...new Set(cattle.map((c) => c.breed).filter(Boolean))]),
    [breedOpts, cattle],
  );
  const originFilterOptions = useMemo(
    () => mergeObservedValues(originOpts, [...new Set(cattle.map((c) => c.origin).filter(Boolean))]),
    [originOpts, cattle],
  );
  const calvingEvidence = useMemo(() => buildCalvingEvidence(cattle, calvingRecs), [cattle, calvingRecs]);
  const accountingSnapshotMonth = filters.accountingSnapshotMonth || '';
  const accountingSnapshotEndDate = accountingSnapshotMonthEndISO(accountingSnapshotMonth);
  const accountingSnapshotLabel = accountingSnapshotEndDate ? formatAccountingMonthEnd(accountingSnapshotMonth) : '';
  const accountingSnapshotInputMonth = accountingSnapshotEndDate ? accountingSnapshotMonth : '';
  const accountingSnapshotMinValue = useMemo(() => accountingSnapshotMinMonth(Date.now()), []);
  const accountingSnapshotMaxValue = useMemo(() => accountingSnapshotMaxMonth(Date.now()), []);
  const cattleForFilters = useMemo(
    () =>
      accountingSnapshotEndDate
        ? accountingSnapshotRows(
            cattle,
            cattleTransfers,
            {
              groupField: 'herd',
              transferEntityIdField: 'cattle_id',
              transferFromField: 'from_herd',
              activeGroups: CATTLE_HERD_KEYS,
            },
            accountingSnapshotMonth,
          )
        : cattle,
    [cattle, cattleTransfers, accountingSnapshotEndDate, accountingSnapshotMonth],
  );
  const wantsLastActivity = hasLastActivitySort || visibleColumns.includes('lastActivity');
  const cattleActivityIds = useMemo(() => cattle.map((cow) => cow.id), [cattle]);
  const cattleActivityLoadKey = useMemo(() => [...cattleActivityIds].sort().join('|'), [cattleActivityIds]);
  const latestActivity = useRecordActivityLatest({
    sb,
    authState: authState as AuthState,
    rolePreview,
    entityType: 'cattle.animal',
    recordIds: cattleActivityIds,
    enabled: wantsLastActivity,
    sourceVersion: herds.data,
  });
  useEffect(() => {
    lastActivityCacheRef.current = lastActivityCache;
  }, [lastActivityCache]);
  useEffect(() => {
    if (!wantsLastActivity) return undefined;
    if (!cattle.length) {
      setLastActivityCache(EMPTY_LAST_ACTIVITY_CACHE);
      setLastActivityNotice(null);
      return undefined;
    }

    const cachedByCowId = normalizeLastActivityCache(lastActivityCacheRef.current).byCowId;
    const hasCompleteCache = cattle.every((cow) => cow && cow.id && hasCachedActivityValue(cachedByCowId, cow.id));
    setLastActivityLoading(!hasCompleteCache && !latestActivity.settled);
    setLastActivityNotice(null);
    return undefined;
  }, [wantsLastActivity, cattleActivityLoadKey, cattle, latestActivity.settled, setLastActivityCache]);
  const processedLastActivitySettlementRef = React.useRef('');
  useEffect(() => {
    if (!wantsLastActivity) {
      processedLastActivitySettlementRef.current = '';
      return;
    }
    if (!cattle.length || !latestActivity.settled) return;
    const settlementId = cattleActivityLoadKey + '|' + latestActivity.settlementKey;
    if (processedLastActivitySettlementRef.current === settlementId) return;
    processedLastActivitySettlementRef.current = settlementId;

    if (latestActivity.error) {
      const error = latestActivity.error as ErrorLike;
      setLastActivityNotice({
        kind: 'warning',
        message:
          'Last Activity could not be loaded. The cattle list is still available. (' + (error.message || error) + ')',
      });
      setLastActivityLoading(false);
      return;
    }

    setLastActivityCache({
      byCowId: latestActivity.byRecordId,
      loadKey: cattleActivityLoadKey,
      loadedAt: new Date().toISOString(),
    });
    const failed = latestActivity.failedRecordIds.length;
    setLastActivityNotice(
      failed > 0
        ? {
            kind: 'warning',
            message:
              'Last Activity could not be loaded for ' +
              failed +
              ' cattle record' +
              (failed === 1 ? '' : 's') +
              '. Those rows sort last.',
          }
        : null,
    );
    setLastActivityLoading(false);
  }, [
    wantsLastActivity,
    cattle.length,
    cattleActivityLoadKey,
    latestActivity.settled,
    latestActivity.settlementKey,
    latestActivity.byRecordId,
    latestActivity.failedRecordIds,
    latestActivity.error,
    setLastActivityCache,
  ]);

  const filtered = useMemo(() => {
    const effectiveFilters = {...filters};
    const predicate = buildCattlePredicate(effectiveFilters, {
      todayMs: Date.now(),
      calvingRecs: calvingEvidence,
      weighIns,
      staleDaysThreshold: STALE_WEIGHT_DAYS_DEFAULT,
    });
    return cattleForFilters.filter(predicate);
  }, [cattleForFilters, filters, calvingEvidence, weighIns]);

  const sortedFlat = useMemo(() => {
    const cmp = buildCattleComparator(sortRules, {
      calvingRecs: calvingEvidence,
      weighIns,
      lastActivityByCowId,
      todayMs: Date.now(),
      nonCalvingCutoffDate: filters.nonCalvingCutoffDate,
    });
    return [...filtered].sort(cmp);
  }, [filtered, sortRules, calvingEvidence, weighIns, lastActivityByCowId, filters.nonCalvingCutoffDate]);

  function cattleHerdExportColumns(): CattleExportColumn[] {
    const female = (cow: CattleHerdRow) => cow.sex === 'cow' || cow.sex === 'heifer';
    const columns: CattleExportColumn[] = [
      ...(accountingSnapshotEndDate
        ? [
            {
              header: 'Snapshot date',
              value: (c: CattleHerdRow) => c._accountingSnapshotEndDate || accountingSnapshotEndDate,
            },
            {
              header: 'Snapshot herd',
              value: (c: CattleHerdRow) => HERD_LABELS[c.herd] || c.herd || '',
            },
          ]
        : []),
      {header: 'Tag', value: (c) => c.tag || ''},
      {
        header: 'Herd',
        value: (c) =>
          (c._accountingSnapshotOriginalGroup && HERD_LABELS[c._accountingSnapshotOriginalGroup]) ||
          c._accountingSnapshotOriginalGroup ||
          HERD_LABELS[c.herd] ||
          c.herd ||
          '',
      },
      {header: 'Sex', value: (c) => c.sex || ''},
      {header: 'Breed', value: (c) => c.breed || ''},
      {header: 'Origin', value: (c) => c.origin || ''},
      {header: 'Age', value: (c) => rowAge(c) || ''},
      {header: 'Birth date', value: (c) => c.birth_date || ''},
      {header: 'Last weight lbs', value: (c) => lastWeight(c) ?? ''},
      {header: 'Last weighed', value: (c) => lastWeightEntryFor(c, weighIns)?.entered_at || ''},
      {header: 'Last activity', value: (c) => lastActivityByCowId[c.id] || ''},
      {header: 'Last calved', value: (c) => (female(c) ? lastCalving(c.tag)?.calving_date || '' : '')},
      {header: 'Calf count', value: (c) => (female(c) ? calfCount(c.tag) : '')},
      {header: 'Breeding status', value: (c) => c.breeding_status || ''},
      {header: 'Breeding blacklist', value: (c) => (c.breeding_blacklist ? 'yes' : 'no')},
      {header: 'Dam tag', value: (c) => c.dam_tag || ''},
      {header: 'Sire tag', value: (c) => c.sire_tag || ''},
      {header: 'Percent Wagyu', value: (c) => c.pct_wagyu ?? ''},
      {header: 'Purchase date', value: (c) => c.purchase_date || ''},
      {header: 'Purchase amount', value: (c) => c.purchase_amount ?? ''},
      {header: 'Sale date', value: (c) => c.sale_date || ''},
      {header: 'Sale amount', value: (c) => c.sale_amount ?? ''},
      {header: 'Death date', value: (c) => c.death_date || ''},
      {header: 'Death reason', value: (c) => c.death_reason || ''},
      {header: 'Record ID', value: (c) => c.id || ''},
    ];
    return columns;
  }

  function handleExportCsv() {
    const columns = cattleHerdExportColumns();
    const ok = downloadCsv(csvFilename('cattle-herds'), rowsToCsv(columns, sortedFlat));
    if (!ok) setNotice({kind: 'error', message: 'CSV export is only available in the browser.'});
  }

  function handlePrintRows() {
    const columns = cattleHerdExportColumns();
    const ok = printRows({
      title: 'Cattle Herds',
      subtitle: accountingSnapshotLabel
        ? sortedFlat.length + ' active cattle at ' + accountingSnapshotLabel
        : sortedFlat.length + ' filtered cattle',
      columns,
      rows: sortedFlat,
    });
    if (!ok) setNotice({kind: 'error', message: 'Print is only available in the browser.'});
  }

  // ── filter chip handlers ───────────────────────────────────────────────────
  function setFilter(key: string, value: unknown) {
    setFilters((prev) => {
      const next = {...prev};
      if (
        value == null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
      ) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }
  function clearFilter(key: string) {
    setFilter(key, null);
  }
  function clearAllFilters() {
    setFilters({});
  }

  function toggleFilterArrayValue(key: string, value: string) {
    setFilters((prev) => {
      const cur = (Array.isArray(prev[key]) ? prev[key] : []) as string[];
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      const out = {...prev};
      if (next.length === 0) delete out[key];
      else out[key] = next;
      return out;
    });
  }

  // ── sort rule handlers ─────────────────────────────────────────────────────
  function addSortRule(key: string) {
    setSortRules((prev) => {
      if (prev.find((r) => r.key === key)) return prev;
      const nextRule = {key, dir: SORT_DEFAULT_DIR[key] || 'asc'};
      const onlyDefaultTag = prev.length === 1 && prev[0].key === 'tag' && prev[0].dir === 'asc';
      if (onlyDefaultTag && key !== 'tag') return [nextRule];
      return [nextRule, ...prev];
    });
  }
  function removeSortRule(idx: number) {
    setSortRules((prev) => prev.filter((_, i) => i !== idx));
  }
  function flipSortDir(idx: number) {
    setSortRules((prev) => prev.map((r, i) => (i === idx ? {...r, dir: r.dir === 'asc' ? 'desc' : 'asc'} : r)));
  }
  function moveSortRule(idx: number, delta: number) {
    setSortRules((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  // ── saved-view handlers ────────────────────────────────────────────────────
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  function applySavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as SavedViewState;
    setFilters(st.filters && typeof st.filters === 'object' ? (st.filters as FilterState) : {});
    setSortRules(Array.isArray(st.sortRules) ? (st.sortRules as SortRule[]) : []);
    setVisibleColumns(
      Array.isArray(st.columns) && st.columns.length > 0 ? (st.columns as string[]) : CATTLE_DEFAULT_COLUMN_KEYS,
    );
    setOpenFilter(null);
    setOpenToolPanel(null);
  }
  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    if (!id) return;
    const view = savedViews.find((v) => v.id === id);
    applySavedView(view);
  }
  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setShowSaveViewForm(true);
  }
  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: CATTLE_HERDS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: {...buildViewState({filters, sortRules, viewMode: 'flat'}), columns: visibleColumns},
      })) as SavedViewRow | null;
      setShowSaveViewForm(false);
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (caught) {
      const e = caught as ErrorLike;
      setNotice({kind: 'error', message: 'Save view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {
        viewState: {...buildViewState({filters, sortRules, viewMode: 'flat'}), columns: visibleColumns},
      });
      await loadSavedViews();
      setNotice({kind: 'success', message: `Updated "${selectedView.name}" to the current filters/sort/view.`});
    } catch (caught) {
      const e = caught as ErrorLike;
      setNotice({kind: 'error', message: 'Update view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }
  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete(`Delete saved view "${selectedView.name}"?`, async () => {
      setSavedViewBusy(true);
      try {
        await deleteSavedView(sb, selectedView.id);
        setSelectedViewId('');
        await loadSavedViews();
      } catch (caught) {
        const e = caught as ErrorLike;
        setNotice({kind: 'error', message: 'Delete view failed: ' + (e.message || String(e))});
      } finally {
        setSavedViewBusy(false);
      }
    });
  }

  // ── Add / Edit / Delete / Transfer / Calving — preserved ───────
  // Single close path for the Add/Edit cow modal. Centralizes the form +
  // edit-id reset alongside the inline add-origin UI state so no close path
  // can leak addingOrigin/newOriginInput into the next modal open.
  function closeCowForm() {
    setNotice(null);
    setShowAddForm(false);
    setForm(null);
    setAddingOrigin(false);
    setNewOriginInput('');
  }
  function openAdd() {
    setNotice(null);
    setForm({...EMPTY_COW});
    setAddingOrigin(false);
    setNewOriginInput('');
    setShowAddForm(true);
  }
  async function saveCow() {
    const currentForm = form as CowForm;
    if (!currentForm.tag.trim()) {
      window._wcfConfirm!(
        'Save cow without a tag? (For unweaned calves; admin can tag later.)',
        () => {
          void proceedSaveCow();
        },
        'Save',
      );
      return;
    }
    await proceedSaveCow();
  }
  async function proceedSaveCow() {
    const currentForm = form as CowForm;
    setSaving(true);
    const isFemale = currentForm.sex === 'cow' || currentForm.sex === 'heifer';
    const rec: CowWrite = {
      tag: currentForm.tag.trim() || null,
      sex: currentForm.sex,
      herd: currentForm.herd,
      breed: currentForm.breed || null,
      breeding_blacklist: !!currentForm.breeding_blacklist,
      pct_wagyu: currentForm.pct_wagyu !== '' ? parseInt(currentForm.pct_wagyu) : null,
      origin: currentForm.origin || null,
      birth_date: currentForm.birth_date || null,
      purchase_date: currentForm.purchase_date || null,
      purchase_amount: currentForm.purchase_amount !== '' ? parseFloat(currentForm.purchase_amount) : null,
      dam_tag: currentForm.dam_tag || null,
      sire_tag: currentForm.sire_tag || null,
      registration_num: currentForm.registration_num || null,
      breeding_status: isFemale ? currentForm.breeding_status || null : null,
      sale_date: currentForm.sale_date || null,
      sale_amount: currentForm.sale_amount !== '' ? parseFloat(currentForm.sale_amount) : null,
      death_date: currentForm.death_date || null,
      death_reason: currentForm.death_reason || null,
      old_tags: (Array.isArray(currentForm.old_tags) ? currentForm.old_tags : [])
        .map(function (t) {
          var tag = String(t.tag || '').trim();
          if (!tag) return null;
          var out: OldTagWrite = {tag: tag};
          if (t.changed_at)
            out.changed_at = t.changed_at.length === 10 ? t.changed_at + 'T12:00:00.000Z' : t.changed_at;
          if (t.source) out.source = t.source;
          return out;
        })
        .filter(Boolean) as OldTagWrite[],
    };
    setNotice(null);
    // Add only: the Edit Cow mode was unreachable (openEdit had no caller) and
    // went in E12a; cows are edited on their record page (CattleAnimalPage).
    let newId: string;
    {
      newId = (rec.tag ? 'c-' + rec.tag : 'c-' + Date.now()) + '-' + Math.random().toString(36).slice(2, 5);
      try {
        await createCattleAnimal.run({row: {id: newId, ...rec}});
      } catch (err) {
        setNotice({kind: 'error', message: 'Save failed: ' + writeErrorMessage(err)});
        setSaving(false);
        return;
      }
      // The record.created Activity row is written by the animal.create
      // operation in the same transaction — migration 245.
    }
    setSaving(false);
    // The `await loadAll()` that stood here is gone: both branches now await a
    // declared invalidation set that CONTAINS this view's resource plus the
    // eleven others that read a cattle row — the breeding page, the batch
    // index, the cattle log preview and both HOME resources among them, none of
    // which a bare `loadAll()` reached.
    closeCowForm();
  }
  function lastCalving(tag: string | null | undefined) {
    return lastCalvingRecordFor(tag, calvingEvidence);
  }
  function calfCount(tag: string | null | undefined) {
    return calfCountFor(tag, calvingEvidence);
  }

  // ── filter / sort UI subcomponents (inline for context) ────────────────────
  // eslint-disable-next-line no-unused-vars -- JSX-only use
  function FilterChip({label, active, onClick, dataAttr}: FilterChipProps) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={active ? chipActiveS : chipBaseS}
        data-filter-chip={dataAttr || undefined}
      >
        {label}
      </button>
    );
  }

  // ── chip helpers (label + active/clear) shared by all filter groups ─────────
  function chipActiveFor(key: string) {
    if (key === 'nonCalving') return filters.nonCalvingCows === true || !!filters.nonCalvingCutoffDate;
    if (key === 'calvedStatus') return 'calvedStatus' in filters || !!filters.calvingWindow;
    return key in filters;
  }
  function clearChip(key: string) {
    if (key === 'nonCalving') {
      clearFilter('nonCalvingCows');
      clearFilter('nonCalvingCutoffDate');
      return;
    }
    if (key === 'calvedStatus') {
      clearFilter('calvedStatus');
      clearFilter('calvingWindow');
      return;
    }
    clearFilter(key);
  }

  function chipLabelFor(key: string) {
    const active = chipActiveFor(key);
    switch (key) {
      case 'herdSet':
        return active
          ? 'Herd: ' + (filters.herdSet || []).map((h) => HERD_LABELS[h as string] || h).join(', ')
          : '+ Herd';
      case 'sex':
        return active
          ? 'Sex: ' +
              (filters.sex || [])
                .map(
                  (s) =>
                    (SEX_OPTIONS.find((o) => o.key === s) || ({} as Partial<(typeof SEX_OPTIONS)[number]>)).label || s,
                )
                .join(', ')
          : '+ Sex';
      case 'ageMonthsRange': {
        if (!active) return '+ Age';
        const range = filters.ageMonthsRange!;
        const r: string[] = [];
        if (range.min != null) r.push('≥' + range.min + 'mo');
        if (range.max != null) r.push('≤' + range.max + 'mo');
        return 'Age: ' + r.join(' ');
      }
      case 'breed':
        return active ? 'Breed: ' + (filters.breed || []).join(', ') : '+ Breed';
      case 'origin':
        return active ? 'Origin: ' + (filters.origin || []).join(', ') : '+ Origin';
      case 'weightTier': {
        if (!active) return '+ Weight';
        const tierLabels: Record<string, string> = {
          hasWeight: 'has weight',
          noWeight: 'no weight',
          staleWeight: 'stale weight',
          staleOrNoWeight: 'stale or no weight',
        };
        return 'Weight: ' + (tierLabels[filters.weightTier as string] || filters.weightTier);
      }
      case 'nonCalving': {
        // Single user-facing control is the "No calf since" date. A legacy
        // persisted nonCalvingCows boolean (no date) still shows the chip active
        // with its 9-month default label, but the checkbox is no longer exposed.
        if (filters.nonCalvingCutoffDate) return 'No calf since ' + filters.nonCalvingCutoffDate;
        if (filters.nonCalvingCows === true) return 'No calf since (9-mo default)';
        return '+ No calf since';
      }
      case 'calvedStatus': {
        if ('calvedStatus' in filters) return 'Calved: ' + (filters.calvedStatus === 'yes' ? 'yes' : 'no');
        if (filters.calvingWindow && filters.calvingWindow.mode === 'noneSince')
          return 'Not calved since ' + filters.calvingWindow.since;
        return '+ Calved';
      }
      case 'lastCalvedRange': {
        if (!active) return '+ Last calved';
        const r = filters.lastCalvedRange!;
        return 'Last calved ' + (r.after ? 'after ' + r.after : '') + (r.before ? ' before ' + r.before : '');
      }
      case 'calfCountRange': {
        if (!active) return '+ Calf count';
        const r = filters.calfCountRange!;
        const parts: string[] = [];
        if (r.min != null) parts.push('≥' + r.min);
        if (r.max != null) parts.push('≤' + r.max);
        return 'Calves ' + parts.join(' ');
      }
      case 'breedingStatus':
        return active
          ? 'Status: ' +
              (filters.breedingStatus || [])
                .map(
                  (s) =>
                    (
                      BREEDING_STATUS_OPTIONS.find((o) => o.key === s) ||
                      ({} as Partial<(typeof BREEDING_STATUS_OPTIONS)[number]>)
                    ).label || s,
                )
                .join(', ')
          : '+ Breeding status';
      case 'breedingBlacklist':
        if (filters.breedingBlacklist === true) return 'Blacklist: only';
        if (filters.breedingBlacklist === false) return 'Blacklist: hide';
        return '+ Blacklist';
      case 'damPresence':
        return active ? 'Dam: ' + filters.damPresence : '+ Dam';
      case 'sirePresence':
        return active ? 'Sire: ' + filters.sirePresence : '+ Sire';
      case 'birthDateRange': {
        if (!active) return '+ Birth date';
        const r = filters.birthDateRange!;
        return 'Born ' + (r.after ? 'after ' + r.after : '') + (r.before ? ' before ' + r.before : '');
      }
      case 'wagyuPctRange': {
        if (!active) return '+ Wagyu %';
        const r = filters.wagyuPctRange!;
        const parts: string[] = [];
        if (r.min != null) parts.push('≥' + r.min);
        if (r.max != null) parts.push('≤' + r.max);
        return 'Wagyu ' + parts.join(' ') + '%';
      }
      case 'weightRange': {
        if (!active) return '+ Weight range';
        const r = filters.weightRange!;
        const parts: string[] = [];
        if (r.min != null) parts.push('≥' + r.min + 'lb');
        if (r.max != null) parts.push('≤' + r.max + 'lb');
        return 'Weight ' + parts.join(' ');
      }
      default:
        return key;
    }
  }

  // Popover filter chip (value picker).
  function renderFilterChip(key: string) {
    const active = chipActiveFor(key);
    const isOpen = openFilter === key;
    return (
      <div key={key} style={{position: 'relative', display: 'inline-block'}}>
        <FilterChip
          label={chipLabelFor(key) + (active ? ' ×' : '')}
          active={active}
          dataAttr={key}
          onClick={(e) => {
            if (active && (e.shiftKey || e.metaKey || e.altKey)) {
              clearChip(key);
              return;
            }
            setOpenFilter(isOpen ? null : key);
          }}
        />
        {isOpen && (
          <FilterChipPopover
            filterKey={key}
            filters={filters}
            setFilters={setFilters}
            setFilter={setFilter}
            clearFilter={clearFilter}
            toggleFilterArrayValue={toggleFilterArrayValue}
            breedFilterOptions={breedFilterOptions}
            originFilterOptions={originFilterOptions}
            onClose={() => setOpenFilter(null)}
          />
        )}
      </div>
    );
  }

  // Checkbox-style boolean filter (a labeled checkbox, not a pill chip) — e.g.
  // Unmatched Calves, which sits in Lineage/Other beside Dam/Sire.
  function renderCheckboxFilter(key: string) {
    const checked = filters[key] === true;
    const label = CHECKBOX_FILTER_LABELS[key] || key;
    return (
      <label
        key={key}
        data-cattle-special-filter={key}
        style={{
          // Pushed to the right edge of its group row so it sits off to the
          // side rather than mid-row among the chip filters.
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 10px',
          borderRadius: 10,
          border: checked ? `1px solid ${CATTLE_ACCENT}` : '1px solid var(--border-strong)',
          background: checked ? 'var(--danger-soft)' : 'var(--surface)',
          color: checked ? CATTLE_ACCENT : 'var(--ink)',
          fontSize: 12,
          fontWeight: checked ? 600 : 500,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setFilter(key, e.target.checked ? true : null)}
          data-cattle-special-filter-checkbox={key}
          style={{margin: 0}}
        />
        <span>{label}</span>
      </label>
    );
  }

  function renderFilterGroups() {
    return FILTER_GROUPS.map((g) => (
      <div
        key={g.key}
        data-filter-group={g.key}
        style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}
      >
        <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600, marginRight: 4, minWidth: 96}}>
          {g.label}
        </span>
        {g.keys.map((key) => (CHECKBOX_FILTER_KEYS.has(key) ? renderCheckboxFilter(key) : renderFilterChip(key)))}
      </div>
    ));
  }

  function sortBar() {
    const used = new Set(sortRules.map((r) => r.key));
    const available = CATTLE_SORT_KEYS.filter((k) => !used.has(k));
    return (
      <div style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}>
        <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600, marginRight: 4}}>Sort:</span>
        {sortRules.length === 0 && (
          <span style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>(none)</span>
        )}
        {sortRules.map((r, i) => (
          <span
            key={r.key}
            data-sort-rule={r.key}
            data-sort-dir={r.dir}
            style={{
              ...chipActiveS,
              cursor: 'default',
              gap: 4,
              border: '1px solid var(--border-strong)',
              background: 'var(--surface)',
              color: 'var(--ink)',
              fontWeight: 500,
            }}
          >
            <span style={{fontWeight: 600, color: CATTLE_ACCENT}}>{i + 1}.</span>
            <span>{SORT_KEY_LABELS[r.key] || r.key}</span>
            <button
              type="button"
              onClick={() => flipSortDir(i)}
              title="Toggle direction"
              style={{
                fontSize: 11,
                background: 'var(--surface-2)',
                border: '1px solid var(--border-strong)',
                borderRadius: 10,
                padding: '0 6px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {(SORT_DIR_LABELS[r.key] || {})[r.dir] || r.dir}
            </button>
            <button
              type="button"
              onClick={() => moveSortRule(i, -1)}
              disabled={i === 0}
              title="Move up"
              style={{
                background: 'none',
                border: 'none',
                color: i === 0 ? 'var(--border-strong)' : 'var(--ink-muted)',
                cursor: i === 0 ? 'not-allowed' : 'pointer',
                fontSize: 12,
                padding: '0 2px',
              }}
            >
              {'▲'}
            </button>
            <button
              type="button"
              onClick={() => moveSortRule(i, 1)}
              disabled={i === sortRules.length - 1}
              title="Move down"
              style={{
                background: 'none',
                border: 'none',
                color: i === sortRules.length - 1 ? 'var(--border-strong)' : 'var(--ink-muted)',
                cursor: i === sortRules.length - 1 ? 'not-allowed' : 'pointer',
                fontSize: 12,
                padding: '0 2px',
              }}
            >
              {'▼'}
            </button>
            <button
              type="button"
              onClick={() => removeSortRule(i)}
              title="Remove sort"
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--danger-ink)',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                padding: '0 4px',
              }}
            >
              {'×'}
            </button>
          </span>
        ))}
        {available.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) {
                addSortRule(e.target.value);
                e.target.value = '';
              }
            }}
            style={{...inpS, width: 'auto', fontSize: 11, padding: '4px 8px'}}
            data-sort-add
          >
            <option value="">+ Sort by…</option>
            {available.map((k) => (
              <option key={k} value={k}>
                {SORT_KEY_LABELS[k] || k}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  }

  // Columns and record activation stay in the shared DataTable. Grouped views
  // omit only the repeated herd value; saved column choices are unchanged.
  const ellipsisCellS: CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    minWidth: 0,
  };
  function cowTableColumns(grouped = false): CattleTableColumn[] {
    const female = (c: CattleHerdRow) => c.sex === 'cow' || c.sex === 'heifer';
    const money = (v: unknown) => (v != null && v !== '' ? '$' + Number(v).toLocaleString() : '—');
    const dateCell = (v: string | null | undefined) => <StatusText tone="muted">{v ? fmt(v) : '—'}</StatusText>;
    const textCell = (v: string | null | undefined) => <StatusText tone="muted">{v || '—'}</StatusText>;
    const columns: CattleTableColumn[] = [
      {
        key: 'tag',
        label: 'Tag',
        primary: true,
        render: (c) => (
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
            <span className="cattle-herds-tag">{c.tag ? '#' + c.tag : '(no tag)'}</span>
            <RuminantFlagBadge row={{breeding_blacklist: c.breeding_blacklist}} />
          </span>
        ),
      },
      {
        key: 'herd',
        label: 'Herd',
        render: (c) => (
          <span style={{fontSize: 11, color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap'}}>
            {(c._accountingSnapshotOriginalGroup && HERD_LABELS[c._accountingSnapshotOriginalGroup]) ||
              HERD_LABELS[c.herd]}
          </span>
        ),
      },
      ...(accountingSnapshotEndDate
        ? [
            {
              key: 'snapshotHerd',
              label: 'Snapshot Herd',
              render: (c: CattleHerdRow) => (
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600, whiteSpace: 'nowrap'}}>
                  {HERD_LABELS[c.herd] || c.herd || '—'}
                </span>
              ),
            },
          ]
        : []),
      {
        key: 'sex',
        label: 'Sex',
        render: (c) => <StatusText tone="muted">{c.sex || '—'}</StatusText>,
      },
      {
        key: 'breed',
        label: 'Breed',
        render: (c) => (
          <StatusText tone="muted" style={ellipsisCellS} title={c.breed || undefined}>
            {c.breed ? c.breed.charAt(0).toUpperCase() + c.breed.slice(1).toLowerCase() : '—'}
          </StatusText>
        ),
      },
      {
        key: 'origin',
        label: 'Origin',
        render: (c) => (
          <StatusText tone="muted" style={ellipsisCellS} title={c.origin || undefined}>
            <span data-cattle-row-origin={c.id}>
              {c.origin ? c.origin.charAt(0).toUpperCase() + c.origin.slice(1).toLowerCase() : '—'}
            </span>
          </StatusText>
        ),
      },
      {
        key: 'age',
        label: 'Age',
        align: 'right',
        render: (c) => <StatusText tone="muted">{rowAge(c) || '—'}</StatusText>,
      },
      {key: 'birthDate', label: 'Birth Date', align: 'right', render: (c) => dateCell(c.birth_date)},
      {
        key: 'lastWeight',
        label: 'Last wt',
        align: 'right',
        render: (c) => {
          const lw = lastWeight(c);
          return lw ? (
            <span className="cattle-herds-weight">{lw.toLocaleString() + ' lb'}</span>
          ) : (
            <StatusText tone="muted">no weigh-in</StatusText>
          );
        },
      },
      {
        key: 'lastWeighed',
        label: 'Last Weighed',
        align: 'right',
        render: (c) => {
          const e = lastWeightEntryFor(c, weighIns);
          return <StatusText tone="muted">{e?.entered_at ? fmt(String(e.entered_at).slice(0, 10)) : '—'}</StatusText>;
        },
      },
      {
        key: 'lastActivity',
        label: 'Last Activity',
        align: 'right',
        render: (c) => <StatusText tone="muted">{formatActivityDateTime(lastActivityByCowId[c.id])}</StatusText>,
      },
      {
        key: 'lastCalved',
        label: 'Last calved',
        render: (c) => {
          const lc = female(c) ? lastCalving(c.tag) : null;
          return <StatusText tone="muted">{lc?.calving_date ? fmt(lc.calving_date) : '—'}</StatusText>;
        },
      },
      {
        key: 'calfCount',
        label: 'Calves',
        align: 'right',
        render: (c) => {
          const cc = female(c) ? calfCount(c.tag) : null;
          return cc == null ? (
            <StatusText tone="muted">—</StatusText>
          ) : (
            <span data-calf-count={cc} style={{fontSize: 12, color: 'var(--text-primary)', fontWeight: 600}}>
              {cc}
            </span>
          );
        },
      },
      {key: 'breedingStatus', label: 'Breeding Status', render: (c) => textCell(c.breeding_status)},
      {
        key: 'breedingBlacklist',
        label: 'Breeding Blacklist',
        render: (c) =>
          c.breeding_blacklist ? <Badge variant="danger">YES</Badge> : <StatusText tone="muted">no</StatusText>,
      },
      {
        key: 'damTag',
        label: 'Dam Tag',
        render: (c) => <StatusText tone="muted">{c.dam_tag ? '#' + c.dam_tag : '—'}</StatusText>,
      },
      {
        key: 'sireTag',
        label: 'Sire Tag',
        render: (c) => <StatusText tone="muted">{c.sire_tag ? '#' + c.sire_tag : '—'}</StatusText>,
      },
      {
        key: 'pctWagyu',
        label: '% Wagyu',
        align: 'right',
        render: (c) => (
          <StatusText tone="muted">
            {c.pct_wagyu != null && (c.pct_wagyu as number | string) !== '' ? c.pct_wagyu + '%' : '—'}
          </StatusText>
        ),
      },
      {key: 'registrationNum', label: 'Registration #', render: (c) => textCell(c.registration_num)},
      {key: 'purchaseDate', label: 'Purchase Date', align: 'right', render: (c) => dateCell(c.purchase_date)},
      {
        key: 'purchaseAmount',
        label: 'Purchase Amount',
        align: 'right',
        render: (c) => <StatusText tone="muted">{money(c.purchase_amount)}</StatusText>,
      },
      {key: 'saleDate', label: 'Sale Date', align: 'right', render: (c) => dateCell(c.sale_date)},
      {
        key: 'saleAmount',
        label: 'Sale Amount',
        align: 'right',
        render: (c) => <StatusText tone="muted">{money(c.sale_amount)}</StatusText>,
      },
      {key: 'deathDate', label: 'Death Date', align: 'right', render: (c) => dateCell(c.death_date)},
      {
        key: 'deathReason',
        label: 'Death Reason',
        render: (c) => (
          <StatusText tone="muted" style={ellipsisCellS}>
            {c.death_reason || '—'}
          </StatusText>
        ),
      },
      {key: 'recordId', label: 'Record ID', render: (c) => <StatusText tone="muted">{c.id || '—'}</StatusText>},
    ];
    return columns.filter((col) => columnVisible(col.key) && !(grouped && col.key === 'herd'));
  }

  // Shared <DataTable> renderer for the cattle list. `navList` is the exact
  // visible-order array (sortedFlat for flat; per-herd cows for grouped) so the
  // record page's sequence nav still walks the on-screen order. The per-row
  // hooks (id="cow-<id>", data-cow-row-tag) and the blacklist stripe and badge are
  // preserved via rowProps + rowStyle.
  // eslint-disable-next-line no-unused-vars -- JSX-only use
  function CattleDataTable({navList, surfaceKey, grouped = false}: CattleDataTableProps) {
    const columns = cowTableColumns(grouped);
    const widths = columns.map((col) => CATTLE_COLUMN_WIDTHS[col.key] || 112);
    const gridColumns = [
      ...columns.map((col, i) => {
        const width = widths[i];
        if (col.key === 'breed') return `minmax(${width}px, 1.2fr)`;
        if (col.key === 'origin') return `minmax(${width}px, 1.3fr)`;
        return `${width}px`;
      }),
      '24px',
    ].join(' ');
    const tableStyle = {
      '--cattle-herds-columns': gridColumns,
      '--cattle-herds-min-width':
        Math.max(980, 40 + 24 + widths.reduce((sum, w) => sum + w, 0) + columns.length * 14) + 'px',
    } as CSSProperties;
    return (
      <div className="cattle-herds-table cattle-table-scroll" style={tableStyle}>
        <DataTable
          surfaceKey={surfaceKey}
          rowKey="id"
          columns={columns}
          rows={navList}
          onRowOpen={(c) => navigate('/cattle/herds/' + c.id, recordSeqNavOptions(navList))}
          rowProps={(c) => ({id: 'cow-' + c.id, 'data-cow-row-tag': c.tag || ''})}
          rowStyle={(cow) => ruminantFlagStyle({breeding_blacklist: cow.breeding_blacklist})}
          card={{
            main: (cow) => (cow.tag ? '#' + cow.tag : 'No Tag'),
            line1: (cow) => [HERD_LABELS[cow.herd] || cow.herd, cow.sex, rowAge(cow)].filter(Boolean).join(' · '),
            line2: (cow) => {
              const weight = lastWeight(cow);
              const isFemale = cow.sex === 'cow' || cow.sex === 'heifer';
              const calving = isFemale ? lastCalving(cow.tag) : null;
              const calves = isFemale ? calfCount(cow.tag) : null;
              return [
                weight != null ? weight.toLocaleString() + ' lb' : 'No Weigh-In',
                calving?.calving_date ? 'Calved ' + fmt(calving.calving_date) : null,
                calves != null ? calves + ' calves' : null,
              ]
                .filter(Boolean)
                .join(' · ');
            },
            badge: (cow) => <RuminantFlagBadge row={{breeding_blacklist: cow.breeding_blacklist}} />,
          }}
        />
      </div>
    );
  }

  // ── derived UI flags ───────────────────────────────────────────────────────
  const activeFilterKeys = activeFilterKeysFor(filters);
  const filterCount = activeFilterKeys.length;
  const hasActiveSort = sortRules.length > 0 && !isDefaultSortRules(sortRules);
  // Grouped-by-herd is the baseline. Only an active FILTER switches to the flat
  // results list; a non-default sort just re-orders cattle WITHIN each herd
  // group (groupedHerdSections slices the comparator-ordered sortedFlat), so a
  // sort alone must not collapse the groups.
  const hasActiveFilters = filterCount > 0;
  const extraHerdKeys = [
    ...new Set(sortedFlat.map((c) => c.herd).filter((h) => h && !CATTLE_ALL_HERD_KEYS.includes(h))),
  ];
  const groupedHerdSections = [...CATTLE_ALL_HERD_KEYS, ...extraHerdKeys]
    .map((h) => ({
      key: h,
      label: HERD_LABELS[h] || h,
      rows: sortedFlat.filter((c) => c.herd === h),
    }))
    .filter((section) => section.rows.length > 0);
  const search = filters.textSearch || '';
  const onFarmCattle = cattle.filter((cow) => CATTLE_HERD_KEYS.includes(cow.herd));
  const onFarmWeight = onFarmCattle.reduce((sum, cow) => {
    const weight = lastWeight(cow);
    return sum + (Number.isFinite(weight) ? (weight as number) : 0);
  }, 0);

  // Saved-view picker partition: my views (any visibility) vs other people's
  // public views. RLS already excludes other people's PRIVATE views from the
  // list, so publicOtherViews is simply public rows I don't own.
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );

  const savedViewRadioLabelS: CSSProperties = ruminantSavedViewRadioLabelStyle;
  // Tools use the same secondary pill; the active tool has an outlined accent.
  const toolButtonS = (active = false): CSSProperties => ({
    padding: '0 13px',
    minHeight: 44,
    borderRadius: 999,
    border: active ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
    background: 'var(--surface)',
    color: active ? 'var(--brand)' : 'var(--ink)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    flex: '0 0 auto',
    gap: 7,
  });

  const toolPanelS: CSSProperties = {
    borderTop: '1px solid var(--border)',
    paddingTop: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  function toggleToolPanel(panel: string) {
    setOpenFilter(null);
    setOpenToolPanel((cur) => (cur === panel ? null : panel));
  }

  return (
    <div className="cattle-redesign cattle-herds-redesign">
      <Header />
      <div className="cattle-page" data-cattle-herds-loaded={loading || loadError ? 'false' : 'true'}>
        <div className="cattle-herds-heading">
          <h1 className="cattle-page-title">Herds</h1>
          <p className="cattle-page-summary">
            {loading ? (
              'Loading cattle…'
            ) : loadError ? (
              'Cattle records unavailable.'
            ) : (
              <>
                <strong>{cattle.length.toLocaleString()}</strong> cattle on record ·{' '}
                <strong>{onFarmCattle.length.toLocaleString()}</strong> on farm ·{' '}
                <strong>{onFarmWeight.toLocaleString()}</strong> lb live
              </>
            )}
          </p>
        </div>
        {!showAddForm && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}
        {!showAddForm && lastActivityNotice && (
          <InlineNotice notice={lastActivityNotice} onDismiss={() => setLastActivityNotice(null)} />
        )}
        {loadError && (
          <div data-cattle-herds-load-error="true">
            <InlineNotice notice={loadError} />
            <Button
              variant="secondary"
              type="button"
              data-cattle-herds-load-retry="1"
              onClick={loadAll}
              style={{marginBottom: 12}}
            >
              Retry
            </Button>
          </div>
        )}

        {!loadError && (
          <>
            {openToolPanel === 'savedViews' && (
              <div
                data-saved-views-row
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
                {savedViewsError ? (
                  <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-saved-views-error>
                    Saved views unavailable. Filters still work.
                  </span>
                ) : (
                  <>
                    <select
                      data-saved-view-select
                      value={selectedViewId}
                      disabled={savedViewsLoading}
                      onChange={(e) => onSelectSavedView(e.target.value)}
                      style={{...inpS, width: 'auto', minWidth: 200, fontSize: 12, padding: '6px 10px'}}
                    >
                      <option value="">{savedViewsLoading ? 'Loading…' : '— Select a saved view —'}</option>
                      {myViews.length > 0 && (
                        <optgroup label="My views">
                          {myViews.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name + (v.visibility === 'public' ? ' · public' : ' · private')}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {publicOtherViews.length > 0 && (
                        <optgroup label="Public views">
                          {publicOtherViews.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {selectedViewIsMine && (
                      <>
                        <Button
                          variant="secondary"
                          type="button"
                          data-saved-view-update
                          onClick={updateSelectedView}
                          disabled={savedViewBusy}
                        >
                          Update to current
                        </Button>
                        <Button
                          variant="danger"
                          type="button"
                          data-saved-view-delete
                          onClick={deleteSelectedView}
                          disabled={savedViewBusy}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                    <span style={{flex: 1}} />
                    <Button
                      variant="secondary"
                      type="button"
                      data-saved-view-save-open
                      onClick={openSaveViewForm}
                      disabled={savedViewBusy}
                    >
                      Save current view
                    </Button>
                  </>
                )}
              </div>
            )}
            {openToolPanel === 'savedViews' && showSaveViewForm && (
              <div
                data-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...inpS, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  variant="secondary"
                  type="button"
                  data-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowSaveViewForm(false)}
                  disabled={savedViewBusy}
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Top toolbar */}
            <div className="cattle-herds-toolbar">
              <div className="cattle-herds-toolbar-row">
                <label className="cattle-herds-search">
                  <HerdsIcon name="search" />
                  <input
                    type="text"
                    aria-label="Search cattle"
                    value={search}
                    onChange={(e) => setFilter('textSearch', e.target.value)}
                    placeholder="Search tag, dam, sire, breed, origin…"
                  />
                </label>
                <span className="cattle-herds-snapshot-wrap">
                  <Button
                    variant="secondary"
                    type="button"
                    className="cattle-button"
                    aria-expanded={openToolPanel === 'snapshot'}
                    aria-controls="cattle-herds-snapshot-picker"
                    onClick={() => toggleToolPanel('snapshot')}
                    style={toolButtonS(openToolPanel === 'snapshot' || !!accountingSnapshotInputMonth)}
                  >
                    <HerdsIcon name="calendar" /> EOM Snapshot
                  </Button>
                  {openToolPanel === 'snapshot' && (
                    <div className="cattle-herds-snapshot-picker" id="cattle-herds-snapshot-picker">
                      <label data-cattle-accounting-snapshot-filter="1" title="Accounting snapshot month">
                        <span>End-of-month snapshot</span>
                        <input
                          type="month"
                          aria-label="Accounting snapshot month"
                          min={accountingSnapshotMinValue}
                          max={accountingSnapshotMaxValue}
                          value={accountingSnapshotInputMonth}
                          onChange={(e) => {
                            const nextMonth = e.target.value;
                            setFilter(
                              'accountingSnapshotMonth',
                              nextMonth && nextMonth <= accountingSnapshotMaxValue ? nextMonth : null,
                            );
                          }}
                          data-cattle-accounting-snapshot-month="1"
                        />
                      </label>
                      {accountingSnapshotInputMonth && (
                        <Button
                          variant="secondary"
                          type="button"
                          className="cattle-button"
                          data-cattle-accounting-snapshot-clear="1"
                          onClick={() => clearFilter('accountingSnapshotMonth')}
                        >
                          Clear snapshot
                        </Button>
                      )}
                    </div>
                  )}
                </span>
                <Button
                  variant="secondary"
                  type="button"
                  className="cattle-button"
                  data-cattle-herds-saved-views-toggle="1"
                  aria-label="Saved views"
                  title="Saved views"
                  aria-expanded={openToolPanel === 'savedViews'}
                  onClick={() => toggleToolPanel('savedViews')}
                  style={toolButtonS(openToolPanel === 'savedViews')}
                >
                  Saved views
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  className="cattle-button"
                  data-cattle-herds-filters-toggle="1"
                  aria-label="Filters"
                  title="Filters"
                  aria-expanded={openToolPanel === 'filters'}
                  onClick={() => toggleToolPanel('filters')}
                  style={toolButtonS(openToolPanel === 'filters')}
                >
                  Filters
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  className="cattle-button"
                  data-cattle-herds-sort-toggle="1"
                  aria-label="Sort"
                  title="Sort"
                  aria-expanded={openToolPanel === 'sort'}
                  onClick={() => toggleToolPanel('sort')}
                  style={toolButtonS(openToolPanel === 'sort')}
                >
                  Sort
                </Button>
                <span style={{position: 'relative', display: 'inline-flex'}}>
                  <Button
                    variant="secondary"
                    type="button"
                    className="cattle-button"
                    data-cattle-herds-columns-toggle="1"
                    aria-label="Columns"
                    title="Columns shown in the list"
                    aria-expanded={openToolPanel === 'columns'}
                    onClick={() => toggleToolPanel('columns')}
                    style={toolButtonS(openToolPanel === 'columns')}
                  >
                    Columns
                  </Button>
                  {openToolPanel === 'columns' && (
                    <div
                      data-cattle-herds-columns
                      data-mobile-popover="1"
                      style={{
                        position: 'absolute',
                        top: 46,
                        left: 0,
                        zIndex: 40,
                        width: 440,
                        maxWidth: '92vw',
                        maxHeight: 520,
                        overflowY: 'auto',
                        background: 'var(--surface)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: 12,
                        boxShadow: 'var(--shadow-menu)',
                        padding: '8px 8px 10px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-muted)',
                          fontWeight: 700,
                          padding: '4px 6px 8px',
                          borderBottom: '1px solid var(--border)',
                          marginBottom: 6,
                        }}
                      >
                        Columns shown ({visibleColumns.length}/{CATTLE_HERD_COLUMNS.length})
                      </div>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 10px'}}>
                        {CATTLE_HERD_COLUMNS.map((col) => (
                          <label
                            key={col.key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 9,
                              padding: '6px 8px',
                              borderRadius: 10,
                              cursor: 'pointer',
                              fontSize: 13,
                              color: 'var(--ink)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.key)}
                              onChange={() => toggleColumn(col.key)}
                              data-cattle-column-toggle={col.key}
                              style={{flex: '0 0 auto', margin: 0, width: 15, height: 15}}
                            />
                            <span>{col.label}</span>
                          </label>
                        ))}
                      </div>
                      <div
                        style={{
                          borderTop: '1px solid var(--border)',
                          marginTop: 6,
                          paddingTop: 8,
                          display: 'flex',
                          gap: 8,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => setVisibleColumns(CATTLE_HERD_COLUMNS.map((c) => c.key))}
                          data-cattle-columns-all
                        >
                          Show all
                        </Button>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => setVisibleColumns(CATTLE_DEFAULT_COLUMN_KEYS)}
                          data-cattle-columns-reset
                        >
                          Reset
                        </Button>
                      </div>
                    </div>
                  )}
                </span>
                <Button
                  variant="secondary"
                  type="button"
                  className="cattle-button"
                  data-cattle-herds-export-csv="1"
                  aria-label="Export CSV"
                  title="Export CSV"
                  onClick={handleExportCsv}
                  disabled={loading}
                >
                  CSV
                </Button>
                <Button
                  variant="secondary"
                  data-cattle-herds-print="1"
                  aria-label="Print"
                  title="Print"
                  onClick={handlePrintRows}
                  disabled={loading}
                >
                  Print
                </Button>
                {/* Table B: Bulk Import also creates, so it is the secondary next
                    to the page's one green. */}
                {canBulkImportLivestock(authState as AccessAuthState) && (
                  <Button variant="secondary" onClick={() => setShowBulkImport(true)}>
                    Bulk Import
                  </Button>
                )}
                {canWriteLivestockTables(authState as AccessAuthState) && (
                  <Button variant="primary" className="cattle-herds-add" onClick={openAdd}>
                    <HerdsIcon name="plus" /> Add Cow
                  </Button>
                )}
              </div>

              {openToolPanel === 'filters' && (
                <div data-cattle-filter-groups style={toolPanelS}>
                  {renderFilterGroups()}
                  {filterCount > 0 && (
                    <div>
                      <Button variant="secondary" type="button" onClick={clearAllFilters}>
                        Clear all filters
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {openToolPanel === 'sort' && <div style={toolPanelS}>{sortBar()}</div>}

              <div style={{fontSize: 11, color: 'var(--ink-muted)'}} data-cattle-match-count>
                {sortedFlat.length} {hasActiveFilters ? (sortedFlat.length === 1 ? 'match' : 'cattle match') : 'cattle'}
                {accountingSnapshotLabel && ' - active at ' + accountingSnapshotLabel}
                {filterCount > 0 && ' · ' + filterCount + ' filter' + (filterCount === 1 ? '' : 's')}
                {hasActiveSort && ' · ' + sortRules.length + ' sort' + (sortRules.length === 1 ? '' : 's')}
              </div>
            </div>

            {showBulkImport && (
              <React.Suspense fallback={null}>
                <CattleBulkImport
                  sb={sb}
                  breedOpts={breedOpts}
                  originOpts={originOpts}
                  existingCattle={cattle}
                  onClose={() => setShowBulkImport(false)}
                  onComplete={loadAll}
                />
              </React.Suspense>
            )}

            {loading && (
              <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>
            )}
            {!loading && cattle.length === 0 && (
              <div
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-card)',
                  boxShadow: 'var(--shadow-card)',
                  padding: '2rem',
                  textAlign: 'center',
                  color: 'var(--ink-muted)',
                  fontSize: 13,
                }}
              >
                No cattle records yet. Click <strong>+ Add Cow</strong> to add your first one, or wait for the Podio
                import.
              </div>
            )}

            {!loading && cattle.length > 0 && hasActiveFilters && (
              <div className="cattle-card" data-cattle-flat-list data-cattle-flat-results="1">
                <div className="cattle-section-header">
                  {sortedFlat.length} {sortedFlat.length === 1 ? 'match' : 'matching cattle'}
                  {lastActivityLoading && hasLastActivitySort && (
                    <span
                      data-cattle-last-activity-loading="1"
                      style={{marginLeft: 8, color: 'var(--ink-faint)', fontWeight: 500}}
                    >
                      Loading Last Activity...
                    </span>
                  )}
                </div>
                {sortedFlat.length === 0 && (
                  <div style={{padding: '2rem', textAlign: 'center', color: 'var(--ink-faint)', fontSize: 13}}>
                    No cattle match the current filter.
                  </div>
                )}
                <CattleDataTable navList={sortedFlat} surfaceKey="cattle-herds-flat" />
              </div>
            )}

            {!loading && cattle.length > 0 && !hasActiveFilters && (
              <div data-cattle-grouped-herds="1" className="cattle-herds-groups">
                {groupedHerdSections.map((section) => {
                  const collapsed = !expandedHerds[section.key];
                  // Total live weight (sum of each animal's latest recorded
                  // weight) for the active herds only; outcome herds don't show
                  // a weight. Animals with no weigh-in contribute nothing.
                  const isActiveHerd = CATTLE_HERD_KEYS.includes(section.key);
                  const herdWeightLbs = isActiveHerd
                    ? section.rows.reduce((sum, c) => {
                        const w = lastWeightFor(c, weighIns);
                        return sum + (Number.isFinite(w) ? (w as number) : 0);
                      }, 0)
                    : 0;
                  return (
                    <section key={section.key} className="cattle-card" data-cattle-herd-section={section.key}>
                      <button
                        type="button"
                        className="cattle-section-header wcf-clickable cattle-herds-group-toggle"
                        onClick={() => setExpandedHerds((prev) => ({...prev, [section.key]: !prev[section.key]}))}
                        data-cattle-herd-toggle={section.key}
                        data-cattle-herd-collapsed={collapsed ? '1' : '0'}
                        aria-expanded={!collapsed}
                        aria-controls={'cattle-herd-table-' + section.key}
                        title={collapsed ? 'Expand ' + section.label : 'Collapse ' + section.label}
                      >
                        <span className={'cattle-herds-chevron' + (collapsed ? '' : ' is-open')}>
                          <HerdsIcon name="chevron" />
                        </span>
                        <RuminantGroupLabel
                          label={section.label}
                          index={CATTLE_HERD_KEYS.indexOf(section.key)}
                          archived={!isActiveHerd}
                        />
                        <span className="cattle-count">{section.rows.length}</span>
                        {herdWeightLbs > 0 && (
                          <span data-cattle-herd-weight={section.key} className="cattle-herds-group-weight">
                            {herdWeightLbs.toLocaleString()} lb
                          </span>
                        )}
                      </button>
                      <div id={'cattle-herd-table-' + section.key} hidden={collapsed}>
                        {!collapsed && (
                          <CattleDataTable navList={section.rows} surfaceKey={'cattle-herds-' + section.key} grouped />
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit cow modal */}
      {showAddForm && form && (
        <div
          onClick={() => {
            closeCowForm();
          }}
          style={modalBackdropStyle({
            alignItems: 'flex-start',
            overflowY: 'auto',
          })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={modalDialogStyle({
              maxWidth: 640,
              marginTop: 40,
            })}
          >
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{fontSize: 15, fontWeight: 600, color: 'var(--text-primary)'}}>{'Add Cow'}</div>
              <button
                onClick={() => {
                  closeCowForm();
                }}
                style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-faint)'}}
              >
                {'×'}
              </button>
            </div>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              <div style={{gridColumn: '1/-1'}}>
                <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
              </div>
              <div>
                <label style={lbl}>Tag #</label>
                <input
                  value={form.tag}
                  onChange={(e) => setForm({...form, tag: e.target.value})}
                  placeholder="Required (or blank for unweaned calf)"
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Sex</label>
                <select value={form.sex} onChange={(e) => setForm({...form, sex: e.target.value})} style={inpS}>
                  <option value="cow">Cow</option>
                  <option value="heifer">Heifer</option>
                  <option value="bull">Bull</option>
                  <option value="steer">Steer</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Herd *</label>
                <select value={form.herd} onChange={(e) => setForm({...form, herd: e.target.value})} style={inpS}>
                  {CATTLE_ALL_HERD_KEYS.map((h) => (
                    <option key={h} value={h}>
                      {HERD_LABELS[h]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Breed</label>
                <select
                  value={form.breed || ''}
                  onChange={(e) => setForm({...form, breed: e.target.value})}
                  style={inpS}
                >
                  <option value="">{'— select —'}</option>
                  {breedOpts
                    .filter(function (b) {
                      return b.active;
                    })
                    .map(function (b) {
                      return React.createElement('option', {key: b.id, value: b.label}, b.label);
                    })}
                  {form.breed &&
                    !breedOpts.some(function (b) {
                      return b.active && b.label === form.breed;
                    }) && <option value={form.breed}>{form.breed + ' (historical)'}</option>}
                </select>
              </div>
              <div>
                <label style={lbl}>% Wagyu</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.pct_wagyu}
                  onChange={(e) => setForm({...form, pct_wagyu: e.target.value})}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Origin</label>
                <select
                  value={form.origin || ''}
                  onChange={function (e) {
                    var v = e.target.value;
                    if (v === '__add__') {
                      // Open the inline add-origin UI; form.origin stays unchanged
                      // until the operator types a name and clicks Save.
                      setNewOriginInput('');
                      setAddingOrigin(true);
                      return;
                    }
                    setForm({...form, origin: v});
                  }}
                  style={inpS}
                >
                  <option value="">{'— select —'}</option>
                  {originOpts
                    .filter(function (o) {
                      return o.active;
                    })
                    .map(function (o) {
                      return React.createElement('option', {key: o.id, value: o.label}, o.label);
                    })}
                  {form.origin &&
                    !originOpts.some(function (o) {
                      return o.active && o.label === form.origin;
                    }) && <option value={form.origin}>{form.origin}</option>}
                  <option value="__add__">{'+ Add new origin…'}</option>
                </select>
                {addingOrigin && (
                  <div style={{display: 'flex', gap: 6, marginTop: 6, alignItems: 'center'}}>
                    <input
                      autoFocus
                      type="text"
                      placeholder="New origin name"
                      value={newOriginInput}
                      onChange={(e) => setNewOriginInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setAddingOrigin(false);
                          setNewOriginInput('');
                        }
                      }}
                      style={{...inpS, flex: 1}}
                    />
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        var name = (newOriginInput || '').trim();
                        if (!name) return;
                        var exists = originOpts.find(function (o) {
                          return o.label.toLowerCase() === name.toLowerCase();
                        });
                        if (exists) {
                          setForm({...form, origin: exists.label});
                          setAddingOrigin(false);
                          setNewOriginInput('');
                          return;
                        }
                        var id = 'origin-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
                        // The error was NEVER checked here. A failed insert
                        // still patched state, selected the phantom origin into
                        // the form, and closed the inline UI — so saving the cow
                        // could persist a row referencing an origin that does
                        // not exist in cattle_origins. Checked now, and the list
                        // is refetched rather than patched locally so the
                        // options come from the same read as everything else.
                        //
                        // The trailing `loadAll()` is gone: `createOrigin.run`
                        // awaits the declared invalidation set, which contains
                        // this view's resource AND the animal record page and
                        // the forecast, both of which read cattle_origins and
                        // neither of which the old bare `loadAll()` reached.
                        createOrigin.run({row: {id: id, label: name, active: true}}).then(
                          function (option) {
                            setForm({...form, origin: (option && option.label) || name});
                            setAddingOrigin(false);
                            setNewOriginInput('');
                          },
                          function (err) {
                            setNotice({
                              kind: 'error',
                              message: 'Could not add origin: ' + writeErrorMessage(err),
                            });
                          },
                        );
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setAddingOrigin(false);
                        setNewOriginInput('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <label style={lbl}>Birth Date</label>
                <input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => setForm({...form, birth_date: e.target.value})}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Purchase Date</label>
                <input
                  type="date"
                  value={form.purchase_date}
                  onChange={(e) => setForm({...form, purchase_date: e.target.value})}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Purchase Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.purchase_amount}
                  onChange={(e) => setForm({...form, purchase_amount: e.target.value})}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Dam Tag #</label>
                <input
                  value={form.dam_tag}
                  onChange={(e) => setForm({...form, dam_tag: e.target.value})}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Sire Tag # / Reg #</label>
                <input
                  value={form.sire_tag}
                  onChange={(e) => setForm({...form, sire_tag: e.target.value})}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Registration #</label>
                <input
                  value={form.registration_num}
                  onChange={(e) => setForm({...form, registration_num: e.target.value})}
                  style={inpS}
                />
              </div>
              {(form.sex === 'cow' || form.sex === 'heifer') && (
                <div>
                  <label style={lbl}>Breeding Status</label>
                  <select
                    value={form.breeding_status || ''}
                    onChange={(e) => setForm({...form, breeding_status: e.target.value})}
                    style={inpS}
                  >
                    <option value="">{'— not set —'}</option>
                    <option value="OPEN">Open</option>
                    <option value="PREGNANT">Pregnant</option>
                    <option value="N/A">N/A</option>
                  </select>
                </div>
              )}

              <div style={{gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
                  <label
                    style={{
                      ...lbl,
                      margin: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--ink)',
                    }}
                  >
                    Prior Tags
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        old_tags: [...(form.old_tags || []), {tag: '', changed_at: '', source: 'manual'}],
                      })
                    }
                    style={{
                      fontSize: 11,
                      color: 'var(--brand)',
                      background: 'none',
                      border: '1px dashed var(--border)',
                      borderRadius: 10,
                      padding: '3px 10px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    + Add Prior Tag
                  </button>
                </div>
                <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 8}}>
                  Tags this cow had before her current one — the purchase tag from the selling farm, plus any tags
                  swapped out over time. Multiple entries supported.
                </div>
                {(form.old_tags || []).length === 0 && (
                  <div style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic', padding: '4px 0'}}>
                    No prior tags recorded.
                  </div>
                )}
                {(form.old_tags || []).map(function (t, ti) {
                  return (
                    <div
                      key={ti}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '100px 140px 1fr 30px',
                        gap: 8,
                        marginBottom: 6,
                        alignItems: 'center',
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Tag #"
                        value={t.tag || ''}
                        onChange={function (e) {
                          var next = (form.old_tags || []).slice();
                          next[ti] = {...next[ti], tag: e.target.value};
                          setForm({...form, old_tags: next});
                        }}
                        style={{...inpS, fontSize: 12, padding: '6px 8px'}}
                      />
                      <input
                        type="date"
                        value={t.changed_at || ''}
                        onChange={function (e) {
                          var next = (form.old_tags || []).slice();
                          next[ti] = {...next[ti], changed_at: e.target.value};
                          setForm({...form, old_tags: next});
                        }}
                        style={{...inpS, fontSize: 12, padding: '6px 8px'}}
                      />
                      <select
                        value={t.source || 'manual'}
                        onChange={function (e) {
                          var next = (form.old_tags || []).slice();
                          next[ti] = {...next[ti], source: e.target.value};
                          setForm({...form, old_tags: next});
                        }}
                        style={{...inpS, fontSize: 12, padding: '6px 8px'}}
                      >
                        <option value="import">Purchase tag (selling farm)</option>
                        <option value="weigh_in">Swapped tag (weigh-in)</option>
                        <option value="manual">Other / manual entry</option>
                      </select>
                      <button
                        type="button"
                        title="Remove"
                        onClick={function () {
                          var next = (form.old_tags || []).filter(function (_, i) {
                            return i !== ti;
                          });
                          setForm({...form, old_tags: next});
                        }}
                        style={{
                          background: 'none',
                          border: '1px solid var(--border)',
                          borderRadius: 10,
                          color: 'var(--danger-ink)',
                          cursor: 'pointer',
                          fontSize: 14,
                          lineHeight: 1,
                          padding: '4px 6px',
                          fontFamily: 'inherit',
                        }}
                      >
                        {'×'}
                      </button>
                    </div>
                  );
                })}
              </div>

              {form.sex !== 'steer' && (
                <div style={{gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4}}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: 'var(--danger-ink)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!form.breeding_blacklist}
                      onChange={(e) => setForm({...form, breeding_blacklist: e.target.checked})}
                      style={{margin: 0, flexShrink: 0}}
                    />
                    <span>Breeding blacklist</span>
                  </label>
                  <div style={{fontSize: 11, color: 'var(--ink-faint)', marginLeft: 26, marginTop: 4}}>
                    Use the Issues section to record why.
                  </div>
                </div>
              )}

              {(form.herd === 'sold' || form.sale_date) && (
                <React.Fragment>
                  <div>
                    <label style={lbl}>Sale Date</label>
                    <input
                      type="date"
                      value={form.sale_date}
                      onChange={(e) => setForm({...form, sale_date: e.target.value})}
                      style={inpS}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Sale Amount ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.sale_amount}
                      onChange={(e) => setForm({...form, sale_amount: e.target.value})}
                      style={inpS}
                    />
                  </div>
                </React.Fragment>
              )}
              {(form.herd === 'deceased' || form.death_date) && (
                <React.Fragment>
                  <div>
                    <label style={lbl}>Death Date</label>
                    <input
                      type="date"
                      value={form.death_date}
                      onChange={(e) => setForm({...form, death_date: e.target.value})}
                      style={inpS}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Death Reason</label>
                    <input
                      value={form.death_reason}
                      onChange={(e) => setForm({...form, death_reason: e.target.value})}
                      style={inpS}
                    />
                  </div>
                </React.Fragment>
              )}
            </div>
            <div style={{padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8}}>
              <Button variant="primary" onClick={saveCow} disabled={saving}>
                {saving ? 'Saving…' : 'Add Cow'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  closeCowForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── FilterChipPopover ────────────────────────────────────────────────────────
function FilterChipPopover({
  filterKey,
  filters,
  setFilters,
  setFilter,
  clearFilter,
  toggleFilterArrayValue,
  breedFilterOptions,
  originFilterOptions,
  onClose,
}: FilterChipPopoverProps) {
  const popS: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    background: 'var(--surface)',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    padding: '10px 12px',
    boxShadow: 'var(--shadow-menu)',
    zIndex: 50,
    minWidth: 260,
  };
  const rowS: CSSProperties = {display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12};
  const choiceRowS: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '18px 1fr',
    columnGap: 8,
    alignItems: 'center',
    marginBottom: 6,
    fontSize: 12,
    width: '100%',
    justifyItems: 'start',
  };
  const choiceTextS: CSSProperties = {minWidth: 0, whiteSpace: 'nowrap'};

  function FemaleHint() {
    return (
      <div style={{fontSize: 10, color: 'var(--ink-muted)', fontStyle: 'italic', marginTop: 4}}>
        Applies to females only.
      </div>
    );
  }

  switch (filterKey) {
    case 'nonCalving':
      // Single user-facing control: the "No calf since" date. Setting it shows
      // mature (30+ month) cows/heifers whose last calving is missing or before
      // the date. No checkbox; no separate Age filter needed.
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>No calf since:</span>
            <input
              type="date"
              value={filters.nonCalvingCutoffDate || ''}
              onChange={(e) => {
                // The date is the only control: setting it clears any legacy
                // persisted boolean so state stays unambiguous.
                setFilter('nonCalvingCutoffDate', e.target.value || null);
                if (e.target.value) clearFilter('nonCalvingCows');
              }}
              data-cattle-noncalving-cutoff
              style={{...inpS, width: 150}}
            />
          </div>
          <div style={{fontSize: 10, color: 'var(--ink-muted)', marginTop: 4}}>
            Cows/heifers 30+ months old whose last calving is missing or before this date.
          </div>
          <PopoverFooter
            onClear={() => {
              clearFilter('nonCalvingCows');
              clearFilter('nonCalvingCutoffDate');
            }}
            onClose={onClose}
          />
        </div>
      );
    case 'herdSet':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          {[...CATTLE_HERD_KEYS, ...CATTLE_OUTCOME_KEYS].map((h) => (
            <label key={h} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={(filters.herdSet || []).includes(h)}
                onChange={() => toggleFilterArrayValue('herdSet', h)}
              />
              <span style={choiceTextS}>{HERD_LABELS[h]}</span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('herdSet')} onClose={onClose} />
        </div>
      );
    case 'sex':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          {SEX_OPTIONS.map((opt) => (
            <label key={opt.key} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={(filters.sex || []).includes(opt.key)}
                onChange={() => toggleFilterArrayValue('sex', opt.key)}
              />
              <span style={choiceTextS}>{opt.label}</span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('sex')} onClose={onClose} />
        </div>
      );
    case 'ageMonthsRange':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>Min months:</span>
            <input
              type="number"
              min="0"
              value={(filters.ageMonthsRange && filters.ageMonthsRange.min) ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : parseInt(e.target.value);
                setFilter('ageMonthsRange', {...(filters.ageMonthsRange || {}), min: v});
              }}
              style={{...inpS, width: 80}}
            />
          </div>
          <div style={rowS}>
            <span>Max months:</span>
            <input
              type="number"
              min="0"
              value={(filters.ageMonthsRange && filters.ageMonthsRange.max) ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : parseInt(e.target.value);
                setFilter('ageMonthsRange', {...(filters.ageMonthsRange || {}), max: v});
              }}
              style={{...inpS, width: 80}}
            />
          </div>
          <PopoverFooter onClear={() => clearFilter('ageMonthsRange')} onClose={onClose} />
        </div>
      );
    case 'calvedStatus':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <label style={{...choiceRowS, cursor: 'pointer'}}>
            <input
              type="radio"
              name="calvedStatus"
              checked={filters.calvedStatus === 'yes' && !filters.calvingWindow}
              onChange={() => {
                clearFilter('calvingWindow');
                setFilter('calvedStatus', 'yes');
              }}
            />
            <span style={choiceTextS}>Has calved</span>
          </label>
          <label style={{...choiceRowS, cursor: 'pointer'}}>
            <input
              type="radio"
              name="calvedStatus"
              checked={filters.calvedStatus === 'no' && !filters.calvingWindow}
              onChange={() => {
                clearFilter('calvingWindow');
                setFilter('calvedStatus', 'no');
              }}
            />
            <span style={choiceTextS}>Never calved</span>
          </label>
          <label style={{...choiceRowS, cursor: 'pointer'}}>
            <input
              type="radio"
              name="calvedStatus"
              checked={!!filters.calvingWindow && filters.calvingWindow.mode === 'noneSince'}
              onChange={() => {
                clearFilter('calvedStatus');
                const yr = new Date().getUTCFullYear();
                setFilter('calvingWindow', {mode: 'noneSince', since: `${yr}-01-01`});
              }}
            />
            <span style={choiceTextS}>Not calved this year</span>
          </label>
          <FemaleHint />
          <PopoverFooter
            onClear={() => {
              clearFilter('calvedStatus');
              clearFilter('calvingWindow');
            }}
            onClose={onClose}
          />
        </div>
      );
    case 'breedingBlacklist':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <label style={{...choiceRowS, cursor: 'pointer'}}>
            <input
              type="radio"
              name="breedingBlacklist"
              checked={filters.breedingBlacklist === true}
              onChange={() => setFilter('breedingBlacklist', true)}
            />
            <span style={choiceTextS}>Only blacklisted</span>
          </label>
          <label style={{...choiceRowS, cursor: 'pointer'}}>
            <input
              type="radio"
              name="breedingBlacklist"
              checked={filters.breedingBlacklist === false}
              onChange={() => setFilter('breedingBlacklist', false)}
            />
            <span style={choiceTextS}>Hide blacklisted</span>
          </label>
          <PopoverFooter onClear={() => clearFilter('breedingBlacklist')} onClose={onClose} />
        </div>
      );
    case 'weightTier':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          {[
            {k: 'hasWeight', label: 'Has weight'},
            {k: 'noWeight', label: 'No weight'},
            {k: 'staleWeight', label: 'Stale weight (>90 days)'},
            {k: 'staleOrNoWeight', label: 'Stale or no weight'},
          ].map((opt) => (
            <label key={opt.k} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="radio"
                name="weightTier"
                checked={filters.weightTier === opt.k}
                onChange={() => setFilter('weightTier', opt.k)}
              />
              <span style={choiceTextS}>{opt.label}</span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('weightTier')} onClose={onClose} />
        </div>
      );
    case 'birthDateRange':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>After:</span>
            <input
              type="date"
              value={(filters.birthDateRange && filters.birthDateRange.after) || ''}
              onChange={(e) =>
                setFilter('birthDateRange', {...(filters.birthDateRange || {}), after: e.target.value || null})
              }
              style={{...inpS, width: 140}}
            />
          </div>
          <div style={rowS}>
            <span>Before:</span>
            <input
              type="date"
              value={(filters.birthDateRange && filters.birthDateRange.before) || ''}
              onChange={(e) =>
                setFilter('birthDateRange', {...(filters.birthDateRange || {}), before: e.target.value || null})
              }
              style={{...inpS, width: 140}}
            />
          </div>
          <PopoverFooter onClear={() => clearFilter('birthDateRange')} onClose={onClose} />
        </div>
      );
    case 'lastCalvedRange':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>After:</span>
            <input
              type="date"
              value={(filters.lastCalvedRange && filters.lastCalvedRange.after) || ''}
              onChange={(e) =>
                setFilter('lastCalvedRange', {...(filters.lastCalvedRange || {}), after: e.target.value || null})
              }
              style={{...inpS, width: 140}}
            />
          </div>
          <div style={rowS}>
            <span>Before:</span>
            <input
              type="date"
              value={(filters.lastCalvedRange && filters.lastCalvedRange.before) || ''}
              onChange={(e) =>
                setFilter('lastCalvedRange', {...(filters.lastCalvedRange || {}), before: e.target.value || null})
              }
              style={{...inpS, width: 140}}
            />
          </div>
          <FemaleHint />
          <PopoverFooter onClear={() => clearFilter('lastCalvedRange')} onClose={onClose} />
        </div>
      );
    case 'calfCountRange':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>Min:</span>
            <input
              type="number"
              min="0"
              value={(filters.calfCountRange && filters.calfCountRange.min) ?? ''}
              onChange={(e) =>
                setFilter('calfCountRange', {
                  ...(filters.calfCountRange || {}),
                  min: e.target.value === '' ? null : parseInt(e.target.value),
                })
              }
              style={{...inpS, width: 80}}
            />
          </div>
          <div style={rowS}>
            <span>Max:</span>
            <input
              type="number"
              min="0"
              value={(filters.calfCountRange && filters.calfCountRange.max) ?? ''}
              onChange={(e) =>
                setFilter('calfCountRange', {
                  ...(filters.calfCountRange || {}),
                  max: e.target.value === '' ? null : parseInt(e.target.value),
                })
              }
              style={{...inpS, width: 80}}
            />
          </div>
          <FemaleHint />
          <PopoverFooter onClear={() => clearFilter('calfCountRange')} onClose={onClose} />
        </div>
      );
    case 'breedingStatus':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          {BREEDING_STATUS_OPTIONS.map((opt) => (
            <label key={opt.key} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={(filters.breedingStatus || []).includes(opt.key)}
                onChange={() => toggleFilterArrayValue('breedingStatus', opt.key)}
              />
              <span style={choiceTextS}>{opt.label}</span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('breedingStatus')} onClose={onClose} />
        </div>
      );
    case 'damPresence':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          {['any', 'present', 'missing'].map((v) => (
            <label key={v} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="radio"
                name="damPresence"
                checked={(filters.damPresence || 'any') === v}
                onChange={() => (v === 'any' ? clearFilter('damPresence') : setFilter('damPresence', v))}
              />
              <span style={choiceTextS}>{v}</span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('damPresence')} onClose={onClose} />
        </div>
      );
    case 'sirePresence':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          {['any', 'present', 'missing'].map((v) => (
            <label key={v} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="radio"
                name="sirePresence"
                checked={(filters.sirePresence || 'any') === v}
                onChange={() => (v === 'any' ? clearFilter('sirePresence') : setFilter('sirePresence', v))}
              />
              <span style={choiceTextS}>{v}</span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('sirePresence')} onClose={onClose} />
        </div>
      );
    case 'weightRange':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>Min lb:</span>
            <input
              type="number"
              min="0"
              value={(filters.weightRange && filters.weightRange.min) ?? ''}
              onChange={(e) =>
                setFilter('weightRange', {
                  ...(filters.weightRange || {}),
                  min: e.target.value === '' ? null : parseFloat(e.target.value),
                })
              }
              style={{...inpS, width: 100}}
            />
          </div>
          <div style={rowS}>
            <span>Max lb:</span>
            <input
              type="number"
              min="0"
              value={(filters.weightRange && filters.weightRange.max) ?? ''}
              onChange={(e) =>
                setFilter('weightRange', {
                  ...(filters.weightRange || {}),
                  max: e.target.value === '' ? null : parseFloat(e.target.value),
                })
              }
              style={{...inpS, width: 100}}
            />
          </div>
          <PopoverFooter onClear={() => clearFilter('weightRange')} onClose={onClose} />
        </div>
      );
    case 'breed':
      return (
        <div style={{...popS, maxHeight: 280, overflowY: 'auto'}} data-filter-popover={filterKey}>
          {breedFilterOptions.map((opt) => (
            <label key={opt.label} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={(filters.breed || []).includes(opt.label)}
                onChange={() => toggleFilterArrayValue('breed', opt.label)}
              />
              <span style={choiceTextS}>
                {opt.label}
                {opt.source === 'historical' && (
                  <em style={{color: 'var(--ink-faint)', marginLeft: 4}}>(historical)</em>
                )}
              </span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('breed')} onClose={onClose} />
        </div>
      );
    case 'origin':
      return (
        <div style={{...popS, maxHeight: 280, overflowY: 'auto'}} data-filter-popover={filterKey}>
          {originFilterOptions.map((opt) => (
            <label key={opt.label} style={{...choiceRowS, cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={(filters.origin || []).includes(opt.label)}
                onChange={() => toggleFilterArrayValue('origin', opt.label)}
              />
              <span style={choiceTextS}>
                {opt.label}
                {opt.source === 'historical' && (
                  <em style={{color: 'var(--ink-faint)', marginLeft: 4}}>(historical)</em>
                )}
              </span>
            </label>
          ))}
          <PopoverFooter onClear={() => clearFilter('origin')} onClose={onClose} />
        </div>
      );
    case 'wagyuPctRange':
      return (
        <div style={popS} data-filter-popover={filterKey}>
          <div style={rowS}>
            <span>Min %:</span>
            <input
              type="number"
              min="0"
              max="100"
              value={(filters.wagyuPctRange && filters.wagyuPctRange.min) ?? ''}
              onChange={(e) =>
                setFilter('wagyuPctRange', {
                  ...(filters.wagyuPctRange || {}),
                  min: e.target.value === '' ? null : parseFloat(e.target.value),
                })
              }
              style={{...inpS, width: 80}}
            />
          </div>
          <div style={rowS}>
            <span>Max %:</span>
            <input
              type="number"
              min="0"
              max="100"
              value={(filters.wagyuPctRange && filters.wagyuPctRange.max) ?? ''}
              onChange={(e) =>
                setFilter('wagyuPctRange', {
                  ...(filters.wagyuPctRange || {}),
                  max: e.target.value === '' ? null : parseFloat(e.target.value),
                })
              }
              style={{...inpS, width: 80}}
            />
          </div>
          <PopoverFooter onClear={() => clearFilter('wagyuPctRange')} onClose={onClose} />
        </div>
      );
    default:
      return null;
  }
}

function PopoverFooter({onClear, onClose}: PopoverFooterProps) {
  return (
    <div style={{display: 'flex', gap: 6, marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--divider)'}}>
      <Button variant="secondary" type="button" onClick={onClear}>
        Clear
      </Button>
      <Button variant="secondary" type="button" onClick={onClose} style={{marginLeft: 'auto'}}>
        Close
      </Button>
    </div>
  );
}

export default CattleHerdsRouter;
